import React, { useId, useState } from 'react';
import { Box, Button, ButtonBase, Popover, Typography } from '@mui/material';
import { ArrowDropDown, AutoAwesomeOutlined, FactCheckOutlined } from '@mui/icons-material';
import { getTrustCommentHeadline } from '../../utils/trustCitationUtils';

/**
 * Header chip for a generated document's source comments ("Sources 7 ▾"), replacing the old
 * column beside the page. Warning-toned when some sections need a source. Opens a popover that
 * lists the same items: clicking one jumps to and selects its section; items that need a source
 * have a one-click "Fix with Inky".
 * @param {object} props - Props.
 * @param {Array<ReturnType<typeof import('../../utils/trustCitationUtils').extractTrustComments>[number]>} props.items - Source items.
 * @param {(item: object) => void} props.onSelect - Go to (and select) the item's section.
 * @param {(item: object) => void} props.onFix - "Fix with Inky" for an item that needs a source.
 * @returns {JSX.Element|null} Chip, or null when the document has no source metadata.
 */
export default function DocumentSourcesChip({ items, onSelect, onFix }) {
  const [anchor, setAnchor] = useState(null);
  const baseId = useId();
  if (!items || items.length === 0) return null;
  const open = Boolean(anchor);
  const needing = items.filter((item) => item.status !== 'cited').length;
  const popoverId = `${baseId}-sources`;
  const titleId = `${baseId}-sources-title`;
  const summary = needing ? `${items.length} sections, ${needing} need${needing === 1 ? 's' : ''} a source` : `${items.length} sections, all cited`;

  return (
    <>
      <ButtonBase
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={open ? popoverId : undefined}
        aria-label={`Sources: ${summary}. Show list`}
        data-warning={needing ? 'true' : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          height: 32,
          pl: 1.25,
          pr: 0.5,
          gap: 0.75,
          flex: 'none',
          borderRadius: 'var(--q-radius-pill, 999px)',
          border: '1px solid',
          borderColor: needing ? 'var(--q-warning, #C77700)' : 'var(--q-border, #E3E0DA)',
          bgcolor: needing ? 'var(--q-warning-soft, #FDEFD6)' : open ? 'var(--q-control-selected, #DCD8D0)' : 'var(--q-surface, #FFFFFF)',
          color: needing ? 'var(--q-warning-text, #7F4A00)' : 'var(--q-text-2, #3D3A35)',
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'inherit',
          '&:hover': { bgcolor: needing ? 'var(--q-warning-soft, #FDEFD6)' : 'var(--q-hover, #ECEAE5)' },
          '&.Mui-focusVisible': { outline: '2px solid var(--q-focus-ring, #4F3BD6)', outlineOffset: 2 },
        }}
      >
        <FactCheckOutlined sx={{ fontSize: 17 }} />
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Sources</Box>
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{needing ? `${needing}/${items.length}` : items.length}</Box>
        <ArrowDropDown sx={{ fontSize: 20, opacity: 0.8 }} />
      </ButtonBase>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': titleId,
            sx: { mt: 0.75, p: 1.5, width: 'min(400px, calc(100vw - 32px))', maxHeight: 'min(70vh, 560px)', borderRadius: 'var(--q-radius-xl, 12px)' },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, px: 0.5, mb: 1 }}>
          <Typography id={titleId} component="h2" sx={{ flex: 1, fontSize: 15, fontWeight: 600 }}>Sources</Typography>
          <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>{summary}</Typography>
        </Box>
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {items.map((item) => {
            const cited = item.status === 'cited';
            const name = item.heading || item.goalPlain || `Section ${item.sectionIndex + 1}`;
            return (
              <Box component="li" key={item.id} sx={{ border: '1px solid var(--q-border, #E3E0DA)', borderRadius: 'var(--q-radius-lg, 10px)', overflow: 'hidden' }}>
                <ButtonBase
                  onClick={() => {
                    setAnchor(null);
                    // After the popover's focus restore (which returns focus to the chip on
                    // close), so the caret and focus end up in the selected section.
                    setTimeout(() => onSelect(item), 0);
                  }}
                  aria-label={`Go to ${name}: ${cited ? 'cited' : 'needs a source'}`}
                  sx={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    px: 1.25,
                    py: 1,
                    fontFamily: 'inherit',
                    '&:hover': { bgcolor: 'var(--q-hover, #ECEAE5)' },
                    '&.Mui-focusVisible': { outline: '2px solid var(--q-focus-ring, #4F3BD6)', outlineOffset: -2 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography component="span" noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600 }}>{name}</Typography>
                    <Box
                      component="span"
                      sx={{
                        flex: 'none',
                        height: 20,
                        px: 0.75,
                        borderRadius: 999,
                        fontSize: 11.5,
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        bgcolor: cited ? 'var(--q-success-soft)' : 'var(--q-warning-soft)',
                        color: cited ? 'var(--q-success-text)' : 'var(--q-warning-text)',
                      }}
                    >
                      {cited ? 'Cited' : 'Needs a source'}
                    </Box>
                  </Box>
                  <Typography component="span" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.25, fontSize: 12.5, color: 'text.secondary' }}>
                    {getTrustCommentHeadline(item)}
                  </Typography>
                  <Typography component="span" sx={{ display: 'block', mt: 0.25, fontSize: 11.5, color: 'text.secondary' }}>
                    Confidence {item.confidence || 'n/a'}
                  </Typography>
                </ButtonBase>
                {!cited && (
                  <Box sx={{ px: 1.25, pb: 1 }}>
                    <Button
                      size="small"
                      startIcon={<AutoAwesomeOutlined sx={{ fontSize: '15px !important' }} />}
                      onClick={() => {
                        setAnchor(null);
                        onFix(item);
                      }}
                      aria-label={`Fix ${name} with Inky`}
                      sx={{
                        height: 28,
                        px: 1.25,
                        borderRadius: 'var(--q-radius-pill, 999px)',
                        bgcolor: 'var(--q-ai-tint, #F9F8FE)',
                        border: '1px dashed var(--q-ai-border, #C9C1F2)',
                        color: 'var(--q-ai-text, #3A28B8)',
                        fontSize: 12.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'var(--q-ai-tint-strong, #F2EFFD)' },
                      }}
                    >
                      Fix with Inky
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
}
